import { useTranslation } from 'react-i18next';
import ModalFrame from '../../../ui/primitives/ModalFrame';
import UserAvatar from '../../../components/UserAvatar';
import { getRealEstateStyle } from '../../../data/realEstate';
import type { MapObject } from '../../../models/mapObject';
import type { MarketplaceListing } from '../../../models/apiTypes';

interface SalePreviewModalProps {
  isOpen: boolean;
  selectedMapObject: MapObject | null;
  listing: MarketplaceListing | null;
  onClose: () => void;
  onViewHouse: () => void;
}

/** Intermediate sale preview modal — closable, shows house image + price + View house CTA. */
export default function SalePreviewModal({ isOpen, selectedMapObject, listing, onClose, onViewHouse }: SalePreviewModalProps) {
  const { t } = useTranslation();

  const saleOwner = selectedMapObject?.owner ?? null;
  const isOwnerVip = saleOwner ? (saleOwner.vip_until ? new Date(saleOwner.vip_until) > new Date() : false) : false;
  const saleRealEstateId = selectedMapObject?.data?.real_estate_id as number | undefined;
  const saleHouseStyle = saleRealEstateId ? getRealEstateStyle(saleRealEstateId) : null;
  const saleMirrorDirection = selectedMapObject?.mirror_direction ?? 'left';
  const saleOwnerName = saleOwner ? (saleOwner.first_name ?? saleOwner.username ?? '') : '';

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      hideHeader={true}
      closeOnBackdropClick={true}
      variant="dialog"
      className={`max-w-[332px] p-0 ${isOwnerVip ? 'border-2 border-accent-alpha-90' : 'border border-white-alpha-6'}`}
    >
      {listing && (
        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <style>{`
            @keyframes salePopHero { from { transform: translateY(6px) scale(0.94); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
            @keyframes salePopUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            @keyframes saleDotPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
          `}</style>

          {/* ── HERO: house on its beach, full-bleed, melts into the card ── */}
          <div
            style={{
              position: 'relative',
              height: 188,
              backgroundColor: '#16203a',
              backgroundImage: `url('/ui/tap-screen/house-background.webp')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 40%',
              borderTopLeftRadius: 23,
              borderTopRightRadius: 23,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            {/* Top vignette — keeps the badge + close legible over bright sky */}
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(4,8,18,0.45) 0%, rgba(4,8,18,0) 32%)',
            }} />

            {/* House sprite — the hero */}
            {saleHouseStyle && (
              <img
                src={saleHouseStyle.image}
                alt=""
                style={{
                  position: 'relative',
                  width: 'auto',
                  height: 138,
                  maxWidth: '74%',
                  objectFit: 'contain',
                  marginBottom: 16,
                  filter: 'drop-shadow(0 12px 16px rgba(0,0,0,0.55))',
                  transform: saleMirrorDirection === 'right' ? 'scaleX(-1)' : 'none',
                  animation: 'salePopHero 0.55s cubic-bezier(0.22,1,0.36,1) both',
                }}
              />
            )}

            {/* Bottom gradient — dissolves the hero into the dark card */}
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(11,17,31,0) 48%, rgba(11,17,31,0.6) 80%, #0B111F 100%)',
            }} />

            {/* FOR SALE badge — glassy pill, top-left */}
            <div style={{
              position: 'absolute', top: 13, left: 13,
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'linear-gradient(135deg, #20b052 0%, #119040 100%)',
              color: '#fff', fontSize: 10.5, fontWeight: 800,
              letterSpacing: '1.3px', textTransform: 'uppercase',
              padding: '6px 13px', borderRadius: 999,
              boxShadow: '0 6px 16px rgba(17,144,64,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c7ffd9', boxShadow: '0 0 8px #aef5c4', animation: 'saleDotPulse 1.6s ease-in-out infinite' }} />
              {t("For sale")}
            </div>

            {/* Close — glassy circle, top-right */}
            <button
              onClick={onClose}
              aria-label={t("Close")}
              style={{
                position: 'absolute', top: 13, right: 13,
                width: 30, height: 30, borderRadius: '50%',
                background: 'rgba(8,12,22,0.5)',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#fff', fontSize: 17, lineHeight: 1, padding: 0,
              }}
            >
              ×
            </button>
          </div>

          {/* ── CONTENT ── */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '2px 22px 22px', gap: 15,
            animation: 'salePopUp 0.45s cubic-bezier(0.22,1,0.36,1) 0.08s both',
          }}>
            {/* Seller — compact contained chip */}
            {saleOwner && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.055)',
                border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: 999, padding: '4px 14px 4px 4px',
              }}>
                <UserAvatar user={saleOwner} size={26} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.92)', fontFamily: 'Lato, sans-serif' }}>
                  {saleOwnerName}
                </span>
                {isOwnerVip && <span style={{ fontSize: 12 }} title="VIP">👑</span>}
              </div>
            )}

            {/* Price — the typographic centerpiece */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '2.4px', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.32)', fontFamily: 'Lato, sans-serif',
              }}>
                {t("Price")}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontFamily: 'Rubik One, sans-serif', lineHeight: 1 }}>
                <span style={{ fontSize: 44, fontWeight: 800, color: '#2ecc63', textShadow: '0 3px 22px rgba(46,204,99,0.4)' }}>
                  ${Math.round(listing.price_usdt)}
                </span>
                <span style={{ fontSize: 17, fontWeight: 800, color: '#2ecc63', opacity: 0.8 }}>USDT</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.42)', fontFamily: 'Lato, sans-serif', marginTop: 3 }}>
                ≈ {listing.price_ton.toFixed(2)} TON
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={onViewHouse}
              style={{
                width: '100%', marginTop: 3,
                padding: '15px 24px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                background: 'linear-gradient(135deg, #FFD84D 0%, #FFA213 100%)',
                border: 'none', borderRadius: 15,
                color: '#241a00', fontSize: 16, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'Lato, sans-serif',
                boxShadow: '0 10px 24px rgba(255,162,19,0.4), inset 0 1px 0 rgba(255,255,255,0.45)',
              }}
            >
              {t("View house")}
              <span style={{ fontSize: 18, lineHeight: 1 }}>→</span>
            </button>
          </div>
        </div>
      )}
    </ModalFrame>
  );
}
